import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavbarBottom from './NavbarBottom';
import no_image_available from '../images/no_image_available.jpg';
import {
    Image,
    ZoomIn,
    Calendar,
    X,
    ChevronLeft,
    ChevronRight,
    Grid3x3,
    List,
    Sparkles
} from 'lucide-react';

const GalleryList = () => {
    const [galleryList, setGalleryList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;

    const urlImg = `${process.env.REACT_APP_BASEURL}/galeri/`;

    useEffect(() => {
        const fetchGallery = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/image_galery`);
                const datas = await response.json();
                setGalleryList(datas.data || []);
            } catch (error) {
                console.error('Error fetching gallery:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchGallery();
    }, []);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = galleryList.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(galleryList.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('id-ID', options);
    };

    // Open image in modal
    const openImageModal = (image) => {
        setSelectedImage(image);
        document.body.style.overflow = 'hidden';
    };

    // Close image modal
    const closeImageModal = () => {
        setSelectedImage(null);
        document.body.style.overflow = 'auto';
    };

    // Navigate images in modal
    const navigateImage = (direction) => {
        if (!selectedImage) return;
        const currentIndex = galleryList.findIndex(img => img.id === selectedImage.id);
        let newIndex = currentIndex + direction;
        if (newIndex < 0) newIndex = galleryList.length - 1;
        if (newIndex >= galleryList.length) newIndex = 0;
        setSelectedImage(galleryList[newIndex]);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedImage) {
                if (e.key === 'Escape') closeImageModal();
                if (e.key === 'ArrowLeft') navigateImage(-1);
                if (e.key === 'ArrowRight') navigateImage(1);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage]);

    return (
        <>
            <NavbarBottom />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Image className="w-8 h-8 text-[#0889c1]" />
                            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'MuseoSansRounded900' }}>
                                <span className="text-[#0889c1]">GALLERY</span>
                                <span className="text-gray-700"> IMAGE</span>
                            </h1>
                        </div>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#0889c1] to-purple-500 mx-auto rounded-full"></div>
                        <p className="mt-4 text-gray-600 text-sm max-w-2xl mx-auto">
                            Dokumentasi kegiatan dan informasi seputar JDIH Kota Magelang
                        </p>
                        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
                            <Sparkles className="w-4 h-4 text-[#0889c1]" />
                            <span className="text-xs font-medium text-gray-600">
                                Total {galleryList.length} Foto
                            </span>
                        </div>
                    </div>

                    {/* View Controls */}
                    <div className="flex justify-between items-center mb-6 bg-white p-3 rounded-xl shadow-md">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Tampilan:</span>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                        ? 'bg-[#0889c1] text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                aria-label="Grid view"
                            >
                                <Grid3x3 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                        ? 'bg-[#0889c1] text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                aria-label="List view"
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="text-sm text-gray-500">
                            Menampilkan {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, galleryList.length)} dari {galleryList.length}
                        </div>
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#0889c1]"></div>
                        </div>
                    ) : (
                        <>
                            {/* Gallery Grid */}
                            <div className={`grid ${viewMode === 'grid'
                                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                                    : 'grid-cols-1'
                                } gap-6`}>
                                {currentItems.length > 0 ? (
                                    currentItems.map((data, index) => (
                                        <div
                                            key={index}
                                            className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ${viewMode === 'list' ? 'flex flex-col sm:flex-row' : ''
                                                }`}
                                        >
                                            <div
                                                className={`relative overflow-hidden cursor-pointer ${viewMode === 'list'
                                                        ? 'sm:w-64 sm:h-48 w-full h-56'
                                                        : 'w-full h-72'
                                                    } bg-gray-100`}
                                                onClick={() => openImageModal(data)}
                                            >
                                                {data?.file ? (
                                                    <img
                                                        src={urlImg + data.file}
                                                        alt={data.name || 'Gallery Image'}
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = no_image_available;
                                                        }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={no_image_available}
                                                        alt="No image available"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}

                                                {/* Gradient Overlay */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                                {/* Zoom Icon */}
                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <div className="bg-white/90 rounded-full p-3 transform scale-0 group-hover:scale-100 transition-transform duration-300">
                                                        <ZoomIn className="w-6 h-6 text-[#0889c1]" />
                                                    </div>
                                                </div>

                                                {/* Image Counter */}
                                                {viewMode === 'grid' && (
                                                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                                                        {index + 1}/{currentItems.length}
                                                    </div>
                                                )}
                                            </div>

                                            <div className={`p-5 ${viewMode === 'list' ? 'flex-1 flex flex-col justify-center' : ''}`}>
                                                {data.name && (
                                                    <h3 className="font-bold text-gray-800 text-sm leading-snug group-hover:text-[#0889c1] transition-colors duration-200">
                                                        {data.name}
                                                    </h3>
                                                )}
                                                {data.tgl && (
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-2">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{formatDate(data.tgl)}</span>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => openImageModal(data)}
                                                    className="mt-3 text-[#0889c1] text-xs font-semibold hover:underline flex items-center gap-1"
                                                >
                                                    Lihat Foto
                                                    <ChevronRight className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-lg">
                                        <Image className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold text-gray-700 mb-2">Belum Ada Foto</h3>
                                        <p className="text-gray-500">Belum ada gallery image yang tersedia</p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-10 flex justify-center">
                                    <nav className="flex items-center gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${currentPage === 1
                                                    ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                                                    : 'text-gray-700 bg-white hover:bg-[#0889c1] hover:text-white shadow-md hover:shadow-lg'
                                                }`}
                                            aria-label="Previous page"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>

                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                                            <button
                                                key={number}
                                                onClick={() => handlePageChange(number)}
                                                className={`flex items-center justify-center min-w-[40px] h-10 px-3 rounded-lg transition-all duration-200 font-medium ${currentPage === number
                                                        ? 'bg-[#0889c1] text-white shadow-lg shadow-[#0889c1]/30'
                                                        : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md hover:shadow-lg'
                                                    }`}
                                                aria-label={`Go to page ${number}`}
                                                aria-current={currentPage === number ? 'page' : undefined}
                                            >
                                                {number}
                                            </button>
                                        ))}

                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${currentPage === totalPages
                                                    ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                                                    : 'text-gray-700 bg-white hover:bg-[#0889c1] hover:text-white shadow-md hover:shadow-lg'
                                                }`}
                                            aria-label="Next page"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </nav>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={closeImageModal}
                >
                    <button
                        onClick={closeImageModal}
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
                        aria-label="Close modal"
                    >
                        <X className="w-8 h-8" />
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
                        className="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200"
                        aria-label="Previous image"
                    >
                        <ChevronLeft className="w-10 h-10" />
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
                        className="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200"
                        aria-label="Next image"
                    >
                        <ChevronRight className="w-10 h-10" />
                    </button>

                    <div
                        className="relative max-w-5xl max-h-[80vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={urlImg + selectedImage.file}
                            alt={selectedImage.name || 'Gallery Image'}
                            className="w-full h-full object-contain rounded-lg"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = no_image_available;
                            }}
                        />
                        {selectedImage.name && (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                                <p className="text-white text-sm font-medium">{selectedImage.name}</p>
                                {selectedImage.tgl && (
                                    <p className="text-gray-300 text-xs mt-1 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {formatDate(selectedImage.tgl)}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default GalleryList;